import { make } from "vuex-pathify"
import _ from "lodash"

import AuthAPI from "@/api/auth"
import OrdersAPI from "@/api/orders"

import * as defaults from "./defaults"

const state = () => {
  return {
    appLoginPending: false,
    appRegistrationPending: false,
    appResetPasswordPending: false,
    appSetPasswordPending: false,

    fbConnectPending: false,
    fbLoginPending: false,
    fbRegistrationPending: false,

    credentialsModel: _.cloneDeep(defaults.CREDENTIALS_MODEL_TEMPLATE),
    registrationModel: _.cloneDeep(defaults.REGISTRATION_MODEL_TEMPLATE),

    token: null,
    user: null,

    subscription: null,
  }
}

const actions = {
  login({ commit, rootGetters, state, dispatch }) {
    if (state.token) {
      return
    }

    commit("SET_APP_LOGIN_PENDING", true)

    const appConfig = rootGetters["app/appConfig"]

    return AuthAPI.login(appConfig, state.credentialsModel)
      .then(response => {
        commit(
          "SET_CREDENTIALS_MODEL",
          _.cloneDeep(defaults.CREDENTIALS_MODEL_TEMPLATE)
        )
        commit("SET_TOKEN", response.access_token)
        commit("SET_USER", response.user)
        dispatch("getSubscription")
      })
      .finally(() => {
        commit("SET_APP_LOGIN_PENDING", false)
      })
  },

  resetPassword({ commit }, email) {
    commit("SET_APP_RESET_PASSWORD_PENDING", true)

    const redirectTo = window.location.origin + "/set-password"

    return AuthAPI.resetPassword(email, redirectTo).finally(() => {
      commit("SET_APP_RESET_PASSWORD_PENDING", false)
    })
  },

  setPassword({ commit }, { password, token }) {
    return AuthAPI.setPassword(password, token)
  },

  getFacebookLogin({ commit, rootGetters, state }, redirectUrl) {
    commit("SET_FB_LOGIN_PENDING", true)

    const appConfig = rootGetters["app/appConfig"]

    return AuthAPI.getFacebookLoginUrl(appConfig, redirectUrl)
      .then(response => {
        return response
      })
      .finally(() => {
        commit("SET_FB_LOGIN_PENDING", false)
      })
  },

  connectWithFacebook({ commit, state }, { code, redirectUrl }) {
    commit("SET_FB_CONNECT_PENDING", true)

    return AuthAPI.connectWithFacebook(code, redirectUrl)
      .then(response => {
        commit("SET_USER", response)
      })
      .finally(() => {
        commit("SET_FB_CONNECT_PENDING", false)
      })
  },

  loginViaFacebookCode({ commit, rootGetters, state }, { code, redirectUrl }) {
    if (state.token) {
      return
    }

    commit("SET_FB_LOGIN_PENDING", true)

    const appConfig = rootGetters["app/appConfig"]

    return AuthAPI.loginViaFacebookCode(appConfig, code, redirectUrl)
      .then(response => {
        commit("SET_TOKEN", response.access_token)
        commit("SET_USER", response.user)
      })
      .finally(() => {
        commit("SET_FB_LOGIN_PENDING", false)
      })
  },

  logout({ commit, dispatch, state }) {
    if (!state.token) {
      return
    }

    dispatch("clearModules", null, { root: true })

    commit("SET_USER", null)
    commit("SET_TOKEN", null)
  },

  register({ commit, rootGetters, state }) {
    if (state.token) {
      return
    }

    commit("SET_APP_REGISTRATION_PENDING", true)

    const appConfig = rootGetters["app/appConfig"]

    return AuthAPI.register(appConfig, state.registrationModel)
      .then(response => {
        commit(
          "SET_REGISTRATION_MODEL",
          _.cloneDeep(defaults.REGISTRATION_MODEL_TEMPLATE)
        )
      })
      .finally(() => {
        commit("SET_APP_REGISTRATION_PENDING", false)
      })
  },

  verifyEmail(_, token) {
    return AuthAPI.verifyEmail(token)
  },

  registerViaFacebookCode(
    { commit, rootGetters, state },
    { code, redirectUrl }
  ) {
    if (state.token) {
      return
    }

    commit("SET_FB_REGISTRATION_PENDING", true)

    const appConfig = rootGetters["app/appConfig"]

    return AuthAPI.registerViaFacebookCode(appConfig, code, redirectUrl)
      .then(response => {
        commit("SET_TOKEN", response.access_token)
        commit("SET_USER", response.user)
      })
      .finally(() => {
        commit("SET_FB_REGISTRATION_PENDING", false)
      })
  },

  updateUser({ commit, state }) {
    return AuthAPI.updateUser(state.user).then(response => {
      commit("SET_USER", response)
    })
  },

  changePassword({ commit, state }, { currentPassword, newPassword }) {
    return AuthAPI.changePassword(currentPassword, newPassword)
  },

  getSubscription({ commit }) {
    return OrdersAPI.getSubscription().then(response => {
      commit("SET_SUBSCRIPTION", response)
    })
  },

  cancelSubscription({ commit }) {
    return OrdersAPI.cancelSubscription().then(response => {
      commit("SET_SUBSCRIPTION", response)
    })
  },

  revokeFacebookConnection({ commit, state }) {
    const user = state.user
    user.userToSocialNetworks = []
    commit("SET_USER", user)
  },
}

const getters = {
  ...make.getters(state),
  isAuthenticated(state) {
    return !!state.token
  },
  isFacebookConnected(state) {
    let result = false

    if (
      state.user &&
      Array.isArray(state.user.userToSocialNetworks) &&
      state.user.userToSocialNetworks.length
    ) {
      state.user.userToSocialNetworks.forEach(sn => {
        if (sn.socialNetwork && sn.socialNetwork.code === "facebook") {
          result = true
        }
      })
    }

    return result
  },
  hasValidSubscription(state) {
    return !!state.subscription
  },
}

const mutations = {
  ...make.mutations(state),
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
